import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import { FiEye, FiTrash2, FiUser, FiMail, FiLayers, FiAlertCircle, FiCheckCircle, FiClock } from "react-icons/fi";

function AllSupportTicketAdmin() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getAllSupportTicketsAdmin`);
      if (res.data.success) setTickets(res.data.tickets || []);
    } catch (err) {
      console.error("Error:", err);
      Swal.fire("Error", "Could not load tickets", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await axios.put(`${baseurl}updateTicketStatus/${id}`, { status: newStatus });
      if (res.data.success) {
        setTickets(tickets.map(t => t.id === id ? { ...t, status: newStatus } : t));
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `Updated to ${newStatus}`, showConfirmButton: false, timer: 1500 });
      }
    } catch (err) { Swal.fire("Error", "Update failed", "error"); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Ticket?",
      text: "Permanent action.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${baseurl}deleteSupportTicket/${id}`);
        if (res.data.success) {
          setTickets(tickets.filter((item) => item.id !== id));
          Swal.fire("Deleted!", "Ticket removed.", "success");
        }
      } catch (err) { Swal.fire("Error", "Failed to delete", "error"); }
    }
  };

  const viewDetails = (ticket) => {
    Swal.fire({
      title: `<span style="font-size:18px">Ticket #TK-${ticket.id}</span>`,
      html: `
        <div class="text-start" style="font-size:14px">
          <p class="mb-1"><strong>Subject:</strong> ${ticket.subject}</p>
          <hr class="my-2"/>
          <p class="mb-1"><strong>Vendor:</strong> ${ticket.business_name}</p>
          <p class="mb-1"><strong>Contact:</strong> ${ticket.vendor_contact_person} (${ticket.vendor_phone})</p>
          <p class="mb-1"><strong>Order ID:</strong> ${ticket.order_id || 'N/A'}</p>
          <div class="p-2 bg-light rounded mt-2 border">${ticket.description}</div>
          ${ticket.attachment_path ? `<img src="${baseurl}${ticket.attachment_path}" class="img-fluid rounded mt-2 border shadow-sm" style="max-height:180px" />` : ''}
        </div>`,
      showCloseButton: true,
      confirmButtonText: "Close",
      width: '500px'
    });
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper admin-support-panel">
        <div className="main-content p-3">
          
          {/* Compact Stat Row (Text Insights) */}
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-3">
              <div className="stat-box bg-white border p-2 rounded shadow-sm d-flex align-items-center gap-2">
                <div className="icon-circle bg-primary-light text-primary"><FiLayers size={14}/></div>
                <div><small className="d-block text-muted">Total</small><b className="h6 mb-0">{tickets.length}</b></div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="stat-box bg-white border p-2 rounded shadow-sm d-flex align-items-center gap-2">
                <div className="icon-circle bg-danger-light text-danger"><FiAlertCircle size={14}/></div>
                <div><small className="d-block text-muted">Open</small><b className="h6 mb-0">{tickets.filter(t => t.status === 'open').length}</b></div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="stat-box bg-white border p-2 rounded shadow-sm d-flex align-items-center gap-2">
                <div className="icon-circle bg-warning-light text-warning"><FiClock size={14}/></div>
                <div><small className="d-block text-muted">Working</small><b className="h6 mb-0">{tickets.filter(t => t.status === 'in-progress').length}</b></div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="stat-box bg-white border p-2 rounded shadow-sm d-flex align-items-center gap-2">
                <div className="icon-circle bg-success-light text-success"><FiCheckCircle size={14}/></div>
                <div><small className="d-block text-muted">Solved</small><b className="h6 mb-0">{tickets.filter(t => t.status === 'resolved').length}</b></div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="card-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold">Ticket Manifest</h6>
              <button className="btn btn-xs btn-outline-secondary" onClick={fetchTickets}>Refresh</button>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 compact-table">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-3">ID</th>
                    <th>Vendor</th>
                    <th>Issue Summary</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th className="text-end pe-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td className="ps-3 text-muted small">#{ticket.id}</td>
                      <td>
                        <div className="fw-bold small">{ticket.business_name}</div>
                        <div className="extra-small text-muted"><FiUser size={10}/> {ticket.vendor_contact_person}</div>
                      </td>
                      <td>
                        <div className="text-truncate small fw-medium" style={{ maxWidth: "160px" }}>{ticket.subject}</div>
                        <span className="extra-small px-1 border rounded text-muted bg-light">{ticket.category}</span>
                      </td>
                      <td><span className={`p-badge ${ticket.priority}`}>{ticket.priority}</span></td>
                      <td>
                        <select 
                          className={`form-select form-select-xs status-select-compact ${ticket.status}`}
                          value={ticket.status}
                          onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                        >
                          <option value="open">Open</option>
                          <option value="in-progress">Working</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="text-end pe-3">
                        <div className="d-flex justify-content-end gap-1">
                          <button className="btn btn-icon btn-light text-primary" onClick={() => viewDetails(ticket)}><FiEye size={14}/></button>
                          <button className="btn btn-icon btn-light text-danger" onClick={() => handleDelete(ticket.id)}><FiTrash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tickets.length === 0 && !loading && <div className="text-center p-4 text-muted small">No active tickets.</div>}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .admin-support-panel { margin-top: 60px; background: #f4f6f9; min-height: 100vh; }
        .extra-small { font-size: 10px; }
        .btn-xs { padding: 2px 8px; font-size: 11px; }
        .btn-icon { width: 28px; height: 28px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 6px; }
        
        .icon-circle { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
        .bg-primary-light { background: #eef2ff; }
        .bg-danger-light { background: #fff1f2; }
        .bg-warning-light { background: #fffbeb; }
        .bg-success-light { background: #f0fdf4; }

        .compact-table thead th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; padding: 10px; border: none; }
        .compact-table tbody td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }

        .p-badge { font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 2px 6px; border-radius: 4px; }
        .p-badge.high { background: #fee2e2; color: #b91c1c; }
        .p-badge.medium { background: #fef3c7; color: #92400e; }
        .p-badge.low { background: #dcfce7; color: #166534; }

        .status-select-compact { font-size: 11px; font-weight: 600; padding: 2px 4px; height: auto; border: 1px solid transparent; width: 105px; border-radius: 4px; cursor: pointer; }
        .status-select-compact.open { background: #fff5f5; color: #e03131; border-color: #ffa8a8; }
        .status-select-compact.in-progress { background: #fff9db; color: #f08c00; border-color: #ffe066; }
        .status-select-compact.resolved { background: #ebfbee; color: #2f9e44; border-color: #b2f2bb; }
        .status-select-compact.closed { background: #f8f9fa; color: #495057; border-color: #dee2e6; }
      `}</style>
    </>
  );
}

export default AllSupportTicketAdmin;